import '../MapPopupTable/MapPopupTable.css'
import temperatureIcon from '../../assets/icons/popup-temperature.svg'
import pressureIcon from '../../assets/icons/popup-pressure.svg'
import visibilityIcon from '../../assets/icons/popup-visibility.svg'
import windIcon from '../../assets/icons/popup-wind.svg'
import ceilingIcon from '../../assets/icons/popup-ceiling.svg'

const MapPopupTable = ({ properties }) => {
  let timeStampVal: string = "NA";
  let sensorIdVal: string = "NA";
  let temperatureVal: string = "NA";
  let ceilingVal: string = "NA";
  let windVal: string = "NA";
  let dirVal: string = "NA";
  let visibilityVal: string = "NA";
  let sensoryTypeVal: string = "NA";
  let dewpointVal: string = "NA";
  let pressureVal: string = "NA";
  let additionalImageURL: string = "NA";
  let rawObservationVal: string = "NA";
  let prunedKeys: Set<string> = new Set();
  let isAviationProduct : boolean = false;

  // Groups for each variable
  const keysToVariables = {
    timestamp: { keys: ["timestamp"], variable: "timeStampVal", defaultValue: "NA", unitKey: "units" },
    sensorId: { keys: ["StationID", "IMEINumberModemID"], variable: "sensorIdVal", defaultValue: "NA", unitKey: "" },
    temperature: { keys: ["Temperature"], variable: "temperatureVal", defaultValue: "NA", unitKey: "units" },
    ceiling: { keys: ["Ceiling"], variable: "ceilingVal", defaultValue: "NA", unitKey: "units" },
    wind: { keys: ["WindSpeed", "Wind Speed"], variable: "windVal", defaultValue: "NA", unitKey: "units" },
    direction: { keys: ["WindDirection", "Wind Direction"], variable: "dirVal", defaultValue: "NA", unitKey: "units" },
    visibility: { keys: ["Visibility"], variable: "visibilityVal", defaultValue: "NA", unitKey: "units" },
    sensorType: { keys: ["SensorType", "Report Type", "airepType"], variable: "sensoryTypeVal", defaultValue: "NA", unitKey: "" },
    dewPoint: { keys: ["DewPointTemperature", "Dew Point Temperature", "DewpointTemperature", "DewPoint"], variable: "dewpointVal", defaultValue: "NA", unitKey: "units" },
    pressure: { keys: ["Altimeter", "Pressure", "StationPressure", "Sea Level Pressure"], variable: "pressureVal", defaultValue: "NA", unitKey: "units" },
    image: { keys: ["ImageData"], variable: "additionalImageURL", defaultValue: "NA", unitKey: "" },
    rawObs: {keys: ["Raw Observation", "RawObservation"], variable: "rawObservationVal", defaultValue: "NA", unitKey: "" }
  };


  // Function to process the values
  function updateVariables(properties: any, useOptionalKey: boolean) {
    // Iterate through each key group in the mapping
    for (let group in keysToVariables) {
      let { keys, variable, defaultValue, unitKey } = keysToVariables[group];
      if(useOptionalKey) unitKey = "unit"
      for (let key of keys) {
        if (key in properties) {          
          let value = properties[key]["value"] ?? defaultValue;
          if (value !== "NA" && !isNaN(Number(value))) {
            value = String(Math.round(value)) + (unitKey && properties[key][unitKey] ? " " + (unitMapping.has(properties[key][unitKey]) ? unitMapping.get(properties[key][unitKey]) : properties[key][unitKey])  : "");
          }
      
          // Dynamically set the variable based on the group
          if (variable === "timeStampVal") timeStampVal = value;
          else if (variable === "temperatureVal") temperatureVal = value;
          else if (variable === "ceilingVal") ceilingVal = value;
          else if (variable === "windVal") windVal = value;
          else if (variable === "visibilityVal") visibilityVal = value;
          else if (variable === "sensoryTypeVal") sensoryTypeVal = value;
          else if (variable === "dewpointVal") dewpointVal = value;
          else if (variable === "pressureVal") pressureVal = value;
          else if (variable === "dirVal") dirVal = value;
          else if (variable === "additionalImageURL") additionalImageURL = value;
          else if (variable === "sensorIdVal") sensorIdVal = value;
          else if (variable === "rawObservationVal") rawObservationVal = value;
          prunedKeys.add(key)
          break;
        }
      }
    }
  }

  let unitMapping = new Map<string, string>()
  unitMapping.set("DEG", "°")
  unitMapping.set("DegN", "°")
  unitMapping.set("F", " °F")
  unitMapping.set("C", " °C")

  //Checking if the popup is for aviation product or weather sensor
  if("SensorType" in properties )  updateVariables(properties, false)
  else isAviationProduct = true


  if(windVal != "NA" && dirVal != "NA"){
    windVal += " / " + dirVal
  }
  
  //Extra processing for TAFS METARS and PIREPS  
  //TAF
  if(sensoryTypeVal == "NA" && "Raw TAF" in properties){
    sensoryTypeVal = "TAF"
    timeStampVal = properties["Issue Time"]
    rawObservationVal = properties["Raw TAF"]
    sensorIdVal = properties["ID"]
    prunedKeys.add("Issue Time")
    prunedKeys.add("Raw TAF")
    prunedKeys.add("ID")
  }
  //METAR
  //This is a special case where we have to display top bubbles as well 
  if(sensoryTypeVal == "NA" && "Report Type" in properties){
    isAviationProduct = false
    updateVariables(properties, true)
    sensoryTypeVal = properties["Report Type"]["value"]
    timeStampVal = properties["timestamp"]["value"]
    rawObservationVal = properties["Raw Observation"]["value"]
    sensorIdVal = properties["STID"]["value"]
    prunedKeys.add("timestamp")
    prunedKeys.add("Report Type")
    prunedKeys.add("Raw Observation")
    prunedKeys.add("STID")
  }
  //PIREP
  if(sensoryTypeVal == "NA" && "airepType" in properties){
    sensoryTypeVal = properties["airepType"]["value"]
    timeStampVal = properties["obsTime"]["value"]
    rawObservationVal = properties["rawOb"]["value"]
    sensorIdVal = properties["icaoId"]["value"]
    prunedKeys.add("obsTime")
    prunedKeys.add("airepType")
    prunedKeys.add("rawOb")
    prunedKeys.add("icaoId")
  }
  //

  let topElementsMeta : Array<Array<string>> = [
    ["Temperature", temperatureIcon, temperatureVal], 
    ["Dew Point", temperatureIcon, dewpointVal], 
    ["Wind", windIcon, windVal],
    ["Pressure", pressureIcon, pressureVal], 
    ["Ceiling", ceilingIcon, ceilingVal], 
    ["Visibility", visibilityIcon, visibilityVal]
  ]

  const capitalizeStr = (str) => {    
    const caps = str?.charAt(0).toUpperCase() + str?.slice(1)
    return caps
  }
  let keys: string[] = []
  const tableData = Object.entries(properties).map(([key, value], index) => {
    keys.push(key)
    if (Object.keys(properties).includes('Raw TAF') && key != 'Unit') {
      if (Object.keys(properties['Unit']).includes(key)) {
        return {
          key,
          value,
          units: properties['Unit'][key],
        }
      }
      return {
        key,
        value,
      }
    } else if (
      properties?.['Report Type'] &&
      properties?.['Report Type'].value == 'METAR'
    ) {
      return { key, value: value.value, units: value.unit }
    } else if (
      (properties?.data && properties.data == 'AIRMET') ||
      properties.data == 'SIGMET' ||
      properties.data == 'AIREP'
    ) {
      if (key == 'altitudeLow1') {
        return { key, value: value.toString() }
      } else {
        return { key, value: value }
      }
    } else if (
      properties.airepType
    ) {
      if (key == 'altitudeLow1') {
        return { key, value: value.toString() }
      } else {
        return { key, value: value.value }
      }
    } else if (properties.Data?.StationID || properties) {
      if (!key.startsWith('CloudLayer')) {
        return { key, value: value.value, units: value.units }
      } else {
        let heightAGL = null
        if (typeof value.value == 'string') {
          return { key, value: value.value, units: null }
        }

        if (value.value.HeightAGL === null) {
          if (value.value.SkyCondition === 'clear') {
            //value.value.SkyCondition
            heightAGL = capitalizeStr(value.value.SkyCondition) //value.value.HeightAGL
          } else {
            value.value.SkyCondition.charAt(0).toUpperCase() +
              value.value.SkyCondition.slice(1)
            heightAGL =
              capitalizeStr(value.value.SkyCondition) + ' at ' + '20000 FT' //value.value.HeightAGL
          }
        } else {
          if (value.value.SkyCondition === 'clear') {
            heightAGL = capitalizeStr(value.value.SkyCondition) //value.value.HeightAGL
          } else if (value.value === 'No Ceilometer') {
            return { key, value: value.value, units: value.units }
          } else {
            const val = value?.value?.HeightAGL?.value ?? "NA"
            const units = value?.value?.HeightAGL?.units ?? "NA"
            heightAGL = `${capitalizeStr(value.value.SkyCondition)} at ${
              typeof value === 'string' ? val.toString() : val
            } ${units}`
          }
        }
        return { key, value: heightAGL, units: value.units }
      }
    } else {
      return { key, value: 'N/A' }
    }
  })
  const order: string[] = [
    'Data',
    'timestamp',
    'Issue Time',
    'Valid Time From',
    'Valid Time To',
    'Valid Time',
    'Site',
    'STID',
    'ID',
    'Cloud Coverage 1',
    'Cloud Coverage 2',
    'Cloud Coverage 3',
    'Cloud Cover',
    'CloudLayer1',
    'CloudLayer2',
    'CloudLayer3',
    'Cloud Layer 1 Value',
    'Cloud Layer 2 Value',
    'Cloud Layer 3 Value',
    'WindSpeed',
    'Wind Speed',
    'Wind Direction',
    'WindDirection',
    'Visibility',
    'Temperature',
    'Dew Point Temperature',
    'DewpointTemperature',
    'Pressure',
    'Sea Level Pressure',
    'Raw TAF',
    'Raw Observation',
  ]
  keys = keys.filter((value) => !order.includes(value))
  const finalTableData = []
  
  for (const key of order) {
    const value = tableData.find((item) => item.key === key)
    if (value && !(prunedKeys.has(key))){
      finalTableData.push(value)
    }
  }
  for (const key of keys) {
    const value = tableData.find((item) => item.key === key)
    if (value && !(prunedKeys.has(key))) {
      finalTableData.push(value)
    }
  }
  
  function displayLabel(key: string): string | undefined {
    const labels: { [key: string]: string } = {
        "SkyConditionCloudLayerCovers": "Sky Condition",
        "SkyConditionCloudLayerHeights": "Cloud Base Height",
        "CloudThickness": "Cloud Thickness",
    };
    return labels[key];  
  }

  function layerMapping(key: string): string | undefined {
    const labels: { [key: string]: string } = {
        "0": "CLR",
        "1": "FEW",
        "2": "FEW",
        "3": "SCT",
        "4": "SCT",
        "5": "BKN",
        "6": "BKN",
        "7": "BKN",
        "8": "OVC"
    };
    return labels[key];  
  }

  function processRawObs(key: string): string[]{
    const newLineSplit = key.split('\n')
    if(newLineSplit.length > 1) return newLineSplit    
    const splitArr = key.split(" ")
    //console.log(splitArr)

    let currStatement = ""
    let processedArr = []
    for(let word of splitArr){
        if(currStatement.length > 0 && (word.startsWith("FM") || word.startsWith("BECMG") || word.startsWith("TEMPO"))){
            processedArr.push(currStatement.slice(0, -1))
            currStatement = ""
        } 
        currStatement += word + " "
    }
    if(currStatement.length > 0) processedArr.push(currStatement.slice(0, -1))
      console.log(processedArr);
      
    return processedArr
  }
  function dataAvailable() {
    if(temperatureVal != "NA" || dewpointVal != "NA" || windVal != "NA" 
    || pressureVal != "NA" || ceilingVal != "NA" || visibilityVal != "NA"
    ) return true;
    return false;
  }

  let keyMapping = new Map<string, string>()
  keyMapping.set("DewPointTemperature","Dew Point") 
  keyMapping.set("OrientationMWS","Orientation MWS")

  const keyFormatting = (key : string ) => {
    if(keyMapping.has(key)) return keyMapping.get(key)
      let result = key[0]
      for (let ptr = 1; ptr < key.length; ptr++) {
        let c = key[ptr];
        if (c == c.toUpperCase()) {
            result += " ";
        }
        result += c;
    }
    return result
  }
  console.log(rawObservationVal);
  
  return (
    <>
      {sensoryTypeVal === "CL61" ?
        <div className="bg-white shadow-md rounded my-1 w-full">
          {/*  className="leading-normal" */}
          <table className="w-full">
            <thead>
                <th  colspan="2" className="w-full p-3 border-b-2 border-gray-200 bg-gray-100 text-gray-600 text-left text-sm uppercase font-normal">
                  Lidar Data
                </th>
            </thead>
            <tbody>
              {finalTableData.map(
                  (item, index) =>
                    item.value !== undefined &&
                    item.value !== '' &&
                    item.value !== 'N/A' &&
                    item.value !== 'NONE' &&
                    (item.key == "timestamp") && (
                      <tr key={index}>
                        <td className="py-3 border-b break-words w-1 border-gray-200 bg-white text-sm">
                          {capitalizeStr(item.key)}
                        </td>
                        <td className="py-3 border-b break-words w-1 border-gray-200 bg-white text-sm">
                          {(typeof item.value === 'string' || (typeof item.value === 'number' && item.value >= 0)) ? item.value : "-"}{' '}

                          {item.value !== null || item.value >= 0 ? item.units : ''}
                        </td>
                      </tr>
                    )
              )}
            <tr className="border-t">
              <td colSpan="2" className="py-3">
                {/* Nested Table */}
                <table className="min-w-full table-auto bg-gray-50 border border-gray-300">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Property</th>
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Layer 1</th>
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Layer 2</th>
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Layer 3</th>
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Layer 4</th>
                      <th className="px-4 py-2 text-left text-sm text-gray-700">Layer 5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalTableData.map(
                    (item, index) =>
                      item.value !== undefined &&
                      item.value !== '' &&
                      item.value !== 'N/A' &&
                      item.value !== 'NONE' &&
                      (item.key !== "StationID" && item.key !== "timestamp" && item.key !== "SensorType" && item.key !== "VerticalVisibility" 
                        && item.key !== "SkyConditionTotalCloudCover" && item.key !== "Precipitation" && item.key !== "Fog" && item.key !== "CloudHeights"
                      ) && (
                        <tr key={index} className="border-t">
                          <td className="p-2 border-gray-200 bg-white text-sm">{displayLabel(item.key)}</td>
                          {
                            item.value.map((data:any) => (
                              <td className="p-2 text-sm">{item.key == "SkyConditionCloudLayerCovers" ? (data >= 0 ? layerMapping(data) : "-") : (data >= 0 ? `${Math.round(data * 3.281)} ft` : "-") }</td>
                            ))
                          }
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
            {finalTableData.map(
                  (item, index) =>
                    item.value !== undefined &&
                    item.value !== '' &&
                    item.value !== 'N/A' &&
                    item.value !== 'NONE' &&
                    (item.key == "StationID" || item.key == "SensorType" || item.key == "VerticalVisibility" || item.key == "Precipitation"  || item.key == "Fog" ) && (
                      <tr key={index}>
                        <td className="py-3 border-b break-words w-1 border-gray-200 bg-white text-sm">
                          {capitalizeStr(item.key)}
                        </td>
                        {
                          (item.key !== "Precipitation"  && item.key !== "Fog") ? 
                          <td className="py-3 border-b break-words w-1 border-gray-200 bg-white text-sm">
                            {(typeof item.value === 'string' || (typeof item.value === 'number' && item.value >= 0)) ? item.value : "-"}{' '}
                            {item.value !== null || item.value >= 0 ? item.units : ''}
                          </td> : 
                          <td className="py-3 border-b break-words w-1 border-gray-200 bg-white text-sm">
                            {(typeof item.value === 'string' || (typeof item.value === 'number' && item.value == 1)) ? "Yes" : "No"}
                          </td>
                        }
                        
                      </tr>
                    )
              )}
            </tbody>   
          </table>   
        </div>
        :
        <div className="flex flex-col gap-2 h-full justify-center w-full">
          <div className='flex gap-2 items-center'>
            <div className="header">
              Weather Data
            </div>
            <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
              {sensoryTypeVal}
            </div>
            {
              sensorIdVal !== "NA" &&
              <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
                {sensorIdVal}
              </div>
            }
          </div>
          
          {
            timeStampVal !== "NA" && 
            <>
              <div className="labelStyling">
                  {timeStampVal}
              </div>
              <hr/>
            </>
          }
          {
            additionalImageURL !== "NA" &&
            <>
              <div className="subHeader">
                Most Recent Image
              </div> 
              <div className="custom shadow-sm">
                  <img src={additionalImageURL} alt="" />
              </div>
              <hr />
            </>
          }
          {
            !isAviationProduct && dataAvailable() &&
            <>
              <div className='grid grid-cols-2 gap-2'>
                {
                  topElementsMeta.map((item, index) => (
                    <>
                      <div className="datafields flex gap-2 bg-black justify-left items-center">
                          <div className="w-8 h-8 flex">
                            <img src={item[1]} alt="Icon" />
                          </div>
                          <div className='flex flex-col'>
                            <div className='labelStyling'>{item[0]}</div>
                            <div className='valueStyling'> {item[2]} </div>
                          </div>
                        </div>
                    </>
                  ))
                }
              </div>
              <hr/>
            </>
          }
          {
            rawObservationVal !== "NA" &&
            <>
              <div className="subHeader w-full h-5 flex gap-2 items-center">
                Raw Data
              </div>
              <div className="min-h-0 max-h-64 p-2 border rounded-lg shadow-sm bg-[#FFFFFF] valueStyling overflow-y-auto">
                {
                  processRawObs(rawObservationVal).map((currStatement, index) => (
                    <>{currStatement}<br/></>
                  )) 
                }
              </div>
            </>
          }
          {
            finalTableData.length > 0 &&
            <>
              <div className="subHeader w-full h-5 flex gap-2 items-center">
                {!isAviationProduct && dataAvailable() && "Additional"} Parameters
              </div>
              <div className="min-h-0 max-h-64 grid grid-cols-2 gap-1 overflow-y-auto p-2 border rounded-lg divide-y shadow-sm bg-[#FFFFFF]">
                {
                  finalTableData.map(
                    (item, index) => 
                      item.value !== undefined &&
                      item.value !== '' &&
                      item.value !== 'N/A' &&
                      item.value !== 'NONE' && 
                      (
                        <div className='p-2'>
                          <div className='labelStyling'>{keyFormatting(item.key)}</div>
                          <div className='valueStyling'>
                            {item.value !== null && item.value ? item.value : "None"}
                            {item.value !== null && item.value && item.units !== undefined ? (unitMapping.has(item.units) ? unitMapping.get(item.units) : ' ' + item.units) : ''}
                          </div>
                        </div>
                      )
                  )
                }
              </div>
            </>
          }
        </div>
      }
    </>
  )
}

export default MapPopupTable
